import { DraftManual, UpdateDraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

interface LockInfo {
  manualId: string;
  lockedBy: string;
  lockedAt: string;
}

interface CreateGlobalManualRequest {
  manualLevel: 'GLOBAL';
  manualTypeId: string;
  verticalId: string;
}

interface CreateJurisdictionManualRequest {
  manualLevel: 'JURISDICTION';
  manualTypeId: string;
  verticalId: string;
  jurisdictionId: string;
  globalManualId: string;
}

interface CreateAgencyManualRequest {
  jurisdictionManualId?: string;
  name: string;
  targetOrgId: string;
}

interface CreateGlobalManualResponse {
  id: string;
  name: string;
  manualLevel: string;
  manualTypeId: string;
  verticalId: string;
}

interface CreateAgencyManualResponse {}

interface CreateJurisdictionManualResponse {
  id: string;
  name: string;
  manualLevel: string;
  manualTypeId: string;
  verticalId: string;
  jurisdictionId: string;
  globalManualId: string;
}

export default function useManageManual() {
  const createGlobalManual = async (
    orgId: string,
    payload: CreateGlobalManualRequest,
    callback?: (response: CreateGlobalManualResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/organizations/${orgId}/manuals`;
    await fetcher({
      url,
      method: 'post',
      body: payload,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const createJurisdictionManual = async (
    orgId: string,
    payload: CreateJurisdictionManualRequest,
    callback?: (response: CreateJurisdictionManualResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/organizations/${orgId}/manuals`;
    await fetcher({
      url,
      method: 'post',
      body: payload,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const createAgencyManual = async (
    orgId: string,
    payload: CreateAgencyManualRequest,
    callback?: (response: CreateAgencyManualResponse) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/organizations/${orgId}/manuals/agency`;
    await fetcher({
      url,
      method: 'post',
      body: payload,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const update = async (
    manualId: string,
    data: DraftManual,
    callback?: (guid: string) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}`;
    try {
      const responseData = await fetcher({
        url,
        method: 'put',
        body: data,
      });
      if (callback) callback(responseData.id);
    } catch (err) {
      if (onError) handleError(err as Error, { onError });
      throw err;
    }
  };

  const updateProperties = async (
    manualId: string,
    data: UpdateDraftManual,
    callback?: (guid: string) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}`;
    await fetcher({
      url,
      method: 'patch',
      body: data,
    })
      .then((data) => {
        if (callback) callback(data.id);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const getLock = async (
    manualId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/lock`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const takeLock = async (
    manualId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/lock`;
    await fetcher({
      url,
      method: 'patch',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const removeLock = async (
    manualId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/lock`;
    await fetcher({
      url,
      method: 'delete',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const getDistributableChanges = async (
    manualId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse-manual-api/v1/manuals/${manualId}/distributable-changes`;
    await fetcher({
      url,
      method: 'get',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  return {
    createGlobalManual,
    createJurisdictionManual,
    createAgencyManual,
    getDistributableChanges,
    getLock,
    removeLock,
    takeLock,
    update,
    updateProperties,
  };
}

export type {
  CreateGlobalManualRequest,
  CreateGlobalManualResponse,
  CreateAgencyManualRequest,
  CreateAgencyManualResponse,
  CreateJurisdictionManualRequest,
  CreateJurisdictionManualResponse,
};
